/**
 * Text helpers for the card SVG. Pure, no DOM, so they run in tests and in the export.
 *
 * Names arrive in Greek or Latin letters. Three traps this file covers:
 *  - Uppercase of Greek drops the tonos only with the 'el' locale (Νίκος becomes ΝΙΚΟΣ, the
 *    default locale keeps ΝΊΚΟΣ, which looks like a typo on a heavy display face).
 *  - Cutting a string at a UTF-16 index splits surrogate pairs and combining marks, so every cut
 *    goes through grapheme clusters.
 *  - The SVG has no layout engine, so the width of a line is estimated from per-glyph advances of
 *    Fira Sans heavy italic. The estimate leans wide (a few percent) and the nameplate also clips,
 *    so the worst case is a clipped letter, never a line that spills over the card.
 */

const GREEK = /[Ͱ-Ͽἀ-῿]/;

/** Card caps for a name: NFC, collapsed spaces, locale-aware uppercase. */
export function cardCaps(raw: string): string {
  const clean = raw.normalize('NFC').replace(/\s+/g, ' ').trim();
  return clean.toLocaleUpperCase(GREEK.test(clean) ? 'el' : 'en');
}

/** Grapheme clusters of a string (code points when Intl.Segmenter is missing). */
export function graphemes(s: string): string[] {
  const Seg = (
    Intl as unknown as {
      Segmenter?: new (
        l?: string,
        o?: { granularity: 'grapheme' },
      ) => { segment(x: string): Iterable<{ segment: string }> };
    }
  ).Segmenter;
  if (Seg) return Array.from(new Seg(undefined, { granularity: 'grapheme' }).segment(s), (x) => x.segment);
  return Array.from(s);
}

/** Advance widths in em for the heavy italic caps face. Anything not listed uses `DEFAULT_ADVANCE`. */
const DEFAULT_ADVANCE = 0.64;
const ADVANCE: Record<string, number> = {
  ' ': 0.3,
  '.': 0.32,
  ',': 0.32,
  "'": 0.3,
  '-': 0.42,
  '…': 0.95,
  '#': 0.66,
  '?': 0.58,
  I: 0.36,
  J: 0.5,
  L: 0.54,
  M: 0.86,
  W: 0.9,
  Ι: 0.36,
  Μ: 0.86,
  Ω: 0.8,
  Ώ: 0.8,
  Δ: 0.68,
  Ξ: 0.62,
};

export function glyphAdvance(g: string): number {
  const first = g[0] ?? '';
  const known = ADVANCE[first];
  if (known !== undefined) return known;
  if (first >= '0' && first <= '9') return 0.62;
  return DEFAULT_ADVANCE;
}

/** Estimated width in SVG units of `text` at `size`. */
export function textWidth(text: string, size: number): number {
  let w = 0;
  for (const g of graphemes(text)) w += glyphAdvance(g);
  return w * size;
}

/** Largest font size from `maxSize` down to `minSize` that fits `maxWidth`, or `minSize` when none does. */
export function fitSize(text: string, maxWidth: number, maxSize: number, minSize: number): number {
  const w1 = textWidth(text, 1);
  if (w1 <= 0) return maxSize;
  const fits = maxWidth / w1;
  return Math.max(minSize, Math.min(maxSize, Math.floor(fits * 10) / 10));
}

/**
 * Cut `text` to fit `maxWidth` at `size`, adding an ellipsis, on a grapheme boundary. A string
 * that already fits is returned as is. Never returns an empty string for a non-empty input.
 */
export function truncateToWidth(text: string, maxWidth: number, size: number): string {
  if (textWidth(text, size) <= maxWidth) return text;
  const parts = graphemes(text);
  const ell = '…';
  let out = '';
  let used = textWidth(ell, size);
  for (const g of parts) {
    const w = textWidth(g, size);
    if (used + w > maxWidth) break;
    out += g;
    used += w;
  }
  out = out.trimEnd();
  return out ? out + ell : (parts[0] ?? '') + ell;
}

/** Name on the nameplate: caps, the biggest size that fits, then truncated at the minimum size. */
export function fitName(
  raw: string,
  maxWidth: number,
  maxSize: number,
  minSize: number,
): { text: string; size: number; truncated: boolean } {
  const caps = cardCaps(raw) || '?';
  const size = fitSize(caps, maxWidth, maxSize, minSize);
  const text = truncateToWidth(caps, maxWidth, size);
  return { text, size, truncated: text !== caps };
}

/** Does this string need the Greek font files in the PNG export? */
export function needsGreek(s: string): boolean {
  return GREEK.test(s);
}

/** Does this string need the latin-ext font files (Turkish, Polish, Vietnamese and friends)? */
export function needsLatinExt(s: string): boolean {
  return /[Ā-ʯḀ-ỿ]/.test(s);
}

/** Stable small hash of a string, for picking one funny line per player. */
export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
